/**
 * DashboardPayLinks — /website-builder/pay-links
 *
 * List + create + copy + toggle + delete reusable Stripe Payment Links.
 *   GET    /api/builder/pay-links
 *   POST   /api/builder/pay-links        {name, price, description?, image_url?}
 *   PUT    /api/builder/pay-links/{id}   {name?, description?, active?}
 *   DELETE /api/builder/pay-links/{id}
 *
 * Each link is a real Stripe `PaymentLink` — the hosted_url is the live URL
 * customers visit to pay.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link2, Plus, Copy, Check, Trash2, Power, ExternalLink, Loader2, DollarSign } from 'lucide-react';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function NewLinkModal({ open, onClose, onCreated }) {
  const [name, setName]         = useState('');
  const [price, setPrice]       = useState('');
  const [description, setDesc]  = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy]         = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const p = parseFloat(price);
    if (!name.trim() || !p || p <= 0) {
      toast.error('Name and price (> 0) required');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/builder/pay-links`,
        { name: name.trim(), price: p, description: description.trim(), image_url: imageUrl.trim() },
        { headers: headers() });
      onCreated(r.data);
      toast.success('Pay link created');
      setName(''); setPrice(''); setDesc(''); setImageUrl('');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create pay link');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#15152E] border border-white/10 rounded-2xl p-7 w-full max-w-md shadow-[0_30px_90px_rgba(0,0,0,0.6)]"
        data-testid="paylink-new-modal"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center">
            <Link2 size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold font-syne text-lg">Create pay link</h3>
            <p className="text-[11.5px] text-white/55">Shareable link for a single product or service.</p>
          </div>
        </div>
        <div className="space-y-3.5">
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">What are you selling? *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Logo design service" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="paylink-modal-name" />
          </div>
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Price (USD) *</Label>
            <div className="relative mt-1.5">
              <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input value={price} onChange={(e) => setPrice(e.target.value)} type="number" step="0.01" min="0" placeholder="250.00" className="bg-white/5 border-white/10 text-white text-sm pl-9 rounded-xl" data-testid="paylink-modal-price" />
            </div>
          </div>
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Description (shown on payment page)</Label>
            <Textarea value={description} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="3 concepts, 2 rounds of revisions, delivered as SVG + PNG." className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl resize-none" data-testid="paylink-modal-description" />
          </div>
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Image URL (optional)</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="paylink-modal-image" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-white/65 hover:text-white" data-testid="paylink-modal-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all disabled:opacity-50 flex items-center gap-2" data-testid="paylink-modal-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Generate Stripe link
          </button>
        </div>
      </form>
    </div>
  );
}

export default function DashboardPayLinks() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [copied, setCopied]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/pay-links`, { headers: headers() });
      setItems(r.data?.items || []);
    } catch { toast.error('Failed to load pay links'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreated = (link) => setItems((arr) => [link, ...arr]);

  const copy = async (l) => {
    try {
      await navigator.clipboard.writeText(l.hosted_url);
      setCopied(l.id);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error('Copy failed'); }
  };

  const toggleActive = async (l) => {
    try {
      const r = await axios.put(`${API}/builder/pay-links/${l.id}`, { active: !l.active }, { headers: headers() });
      setItems((arr) => arr.map((x) => x.id === l.id ? r.data : x));
      toast.success(r.data.active ? 'Link activated' : 'Link deactivated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (l) => {
    if (!(await themedConfirm(`Delete pay link "${l.name}"?\n\nThe Stripe link will be deactivated. Existing buyers can't reuse it.`))) return;
    try {
      await axios.delete(`${API}/builder/pay-links/${l.id}`, { headers: headers() });
      setItems((arr) => arr.filter((x) => x.id !== l.id));
      toast.success('Pay link deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Invoicing"
        title="Pay Links"
        subtitle="Shareable Stripe links that let anyone pay you without an invoice."
        actions={<SqsButton variant="primary" size="sm" icon={Plus} onClick={() => setOpenNew(true)} data-testid="paylinks-new">New Pay Link</SqsButton>}
      />
      <div className="p-10 max-w-5xl" data-testid="paylinks-page">
        {loading ? (
          <div className="flex items-center gap-2 text-white/55"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-[#15152E] border border-white/[0.06] rounded-xl p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#A78BFA]/10 flex items-center justify-center mx-auto mb-4">
              <Link2 size={22} className="text-[#A78BFA]" />
            </div>
            <h3 className="text-[16px] font-bold text-white font-syne">No pay links yet</h3>
            <p className="text-[#9CA3AF] text-[12.5px] mt-1 max-w-md mx-auto">Create a Stripe-backed pay link for any product or service. Perfect for deposits, one-off work, donations, and pop-up offers.</p>
            <button onClick={() => setOpenNew(true)} className="mt-5 px-5 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="paylinks-empty-create">Create your first pay link</button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((l) => (
              <div key={l.id} className={`bg-[#15152E] border rounded-xl px-5 py-4 transition-all ${l.active ? 'border-white/[0.06]' : 'border-white/5 opacity-60'}`} data-testid={`paylink-row-${l.id}`}>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center shrink-0">
                    <Link2 size={16} className="text-[#A78BFA]" />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <p className="text-[13.5px] font-semibold text-white truncate">{l.name}</p>
                      {!l.active && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/8 text-white/55">Inactive</span>}
                    </div>
                    <p className="text-[11.5px] text-[#9CA3AF] truncate" title={l.hosted_url}>${(l.price ?? 0).toFixed(2)} · {l.hosted_url}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <a href={l.hosted_url} target="_blank" rel="noreferrer" className="p-2 rounded-md text-white/65 hover:text-white hover:bg-white/5 transition-colors" title="Open hosted page" data-testid={`paylink-open-${l.id}`}>
                      <ExternalLink size={14} />
                    </a>
                    <button onClick={() => copy(l)} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white bg-white/[0.04] hover:bg-white/[0.08] rounded-md flex items-center gap-1.5" data-testid={`paylink-copy-${l.id}`}>
                      {copied === l.id ? <Check size={12} /> : <Copy size={12} />} {copied === l.id ? 'Copied' : 'Copy'}
                    </button>
                    <button onClick={() => toggleActive(l)} className={`p-2 rounded-md transition-colors ${l.active ? 'text-emerald-300 hover:bg-emerald-500/10' : 'text-white/40 hover:bg-white/5'}`} title={l.active ? 'Deactivate' : 'Activate'} data-testid={`paylink-toggle-${l.id}`}>
                      <Power size={14} />
                    </button>
                    <button onClick={() => remove(l)} className="p-2 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete" data-testid={`paylink-delete-${l.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <NewLinkModal open={openNew} onClose={() => setOpenNew(false)} onCreated={handleCreated} />
    </>
  );
}
